import React from 'react';
import { Card, Flex, Grid, Skeleton, Tabs } from 'antd';
import { useTranslation } from 'react-i18next';
import { NS } from '../../i18n';
import { BasicInformationSkeleton } from '../BasicInformation/BasicInformationSkeleton';
import { BillingPlanSkeleton } from '../BillingPlan/BillingPlanSkeleton';

const { useBreakpoint } = Grid;

/**
 * /profile while its chunk loads, in the page's own frame: the user header
 * (56px avatar, name, email, role tag), Basic information, and the card with
 * the tabs (real, translated labels) over the Usage & Payment tab the page
 * opens on. Basic information and the tab content are the sections' own
 * skeletons; the header card and the page frame mirror Profile.tsx by hand.
 * The route's Suspense fallback and RouteSkeleton's for /profile.
 */
export const ProfileSkeleton: React.FC = () => {
  const { t } = useTranslation(NS);
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  return (
    <Flex
      vertical
      gap={16}
      aria-busy="true"
      aria-label={t('profile.loadingAria')}
      style={{ margin: isMobile ? 'var(--silia-space-sm)' : 'var(--silia-space-lg)' }}
    >
      <Card
        style={{ border: '1px solid var(--silia-split)', borderRadius: '8px' }}
        styles={{ body: { padding: '24px' } }}
      >
        <Flex align="center" gap={16}>
          <Skeleton.Avatar
            active
            shape="square"
            size={56}
            style={{ borderRadius: '8.649px', flexShrink: 0 }}
          />
          <Flex vertical gap={8} style={{ flex: 1, minWidth: 0 }}>
            <Skeleton.Input active style={{ width: 180, minWidth: 0, height: 24 }} />
            <Skeleton.Input active size="small" style={{ width: 220, minWidth: 0, height: 16 }} />
          </Flex>
          <Skeleton.Button active size="small" style={{ width: 72, height: 22 }} />
        </Flex>
      </Card>

      <BasicInformationSkeleton isMobile={isMobile} />

      <Card
        style={{ borderRadius: '8px' }}
        styles={{ body: { padding: isMobile ? '12px' : '24px' } }}
      >
        <Tabs
          activeKey="billing"
          items={[
            {
              key: 'billing',
              label: t('settings.tabs.usagePayment'),
              children: <BillingPlanSkeleton isMobile={isMobile} count={2} />,
            },
            { key: 'invoices', label: t('settings.tabs.invoices') },
          ]}
        />
      </Card>
    </Flex>
  );
};
